import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import TkStepper from "../../components/atoms/TkStepper";
import TkInfoManual from "../../components/atoms/TkInfoManual";
import TkFakeTable from "../../components/molecules/TkFakeTable";
import { GlobalContext } from "../../context/globalContext";
import { BusinessContext } from "../../context/businessContext";
import TkMessageDialog from "../../components/molecules/TkMessageDialog";
import { TkGetCode } from "../../utils/TkGetCode";
import {
  CheckTextSelect,
  DemanderRelationship,
  DepositType,
  FormClassRetire,
  Prefectures,
} from "../../assets/data/Constants";
import { IJob_41Props } from "../../type/contextType/businessContext/IJob_41";
import { IAPI_RET004_Response, IAPI_RET005_Request } from "../../type/apiType/";
import { httpAPI_RET004, httpAPI_RET005 } from "./api/request";
import { steps, fakeTableList } from "./parts";
import TkDateDialog from "../../components/molecules/TkDateDialog";
import authorityUserType from "../../assets/data/TkAuthorityProperty";
import { labelids } from "../../assets/data/TkExplanationProperty";
import TkBackButton from "../../components/molecules/TkBackButton";
import { API_RET005_BuildReqParams } from "../../utils/buildRequestParams";
import { changeZipcodeFormat } from "../../utils/useTools";
import "./_style.scss";
import _ from "lodash";

/**
 * SCRR410027 退職手当金受領者の変更手続き（登録内容の確認）
 * @return SCRR410027 退職手当金受領者の変更手続き（登録内容の確認）
 */
const SCRR410027 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const { state: businessState } = useContext(BusinessContext);

  const [open, setOpen] = useState(false);
  const [httpReq, setHttpReq] = useState<IAPI_RET005_Request>();

  const [stateTableList, setStateTableList] = useState<any>([]);
  const tableListRef = useRef(_.cloneDeep(fakeTableList));

  const isKiko =
    state.authInfo?.userType === authorityUserType.userTypeNumber.KIKO;
  const context = businessState.job_41;
  const demanderRelationship = context?.demanderRelationship; // 続柄
  const applyId = location.state?.applyId;

  const handleReferInit = async () => {
    let data: IJob_41Props & IAPI_RET004_Response = { ...context };

    // 法人ホームの手続履歴から遷移した場合
    if (applyId) {
      // 退職手当金請求参照APIを呼び出す
      try {
        const res = await httpAPI_RET004({
          applyId,
          contractorNo: state.authInfo?.contractorNo,
        });

        if (res.resultCd === "I0000") {
          data = res;
        } else {
          setState({
            ...state,
            errorMsgId: res.resultCd,
          });
          return;
        }
      } catch {
        navigate("/error");
      }
    }

    let resArr = tableListRef.current;

    for (let i in data) {
      let targetObj = resArr.find((item) => item.keyword === i)!;

      if (targetObj) {
        const value = data[i as keyof typeof data];

        if (i === "demanderZipcode") {
          targetObj.text = changeZipcodeFormat(value as string);
          continue;
        }
        if (i === "depositType") {
          targetObj.text = TkGetCode(DepositType, value);
          continue;
        }
        if (i === "identificationDocsKind") {
          targetObj.text = TkGetCode(CheckTextSelect, value);
          continue;
        }

        targetObj.text = value;
      }
    }

    resArr.find((item) => item.keyword === "locationGroup")!.text =
      (TkGetCode(Prefectures, data?.demanderPrefectureCd) ?? "") +
      " " +
      (data?.demanderCity ?? "") +
      " " +
      (data?.demanderAddressOthers ?? "");

    data?.fileList?.forEach((item) => {
      let targetObj = resArr.find((i) => i.id === item.formClass);

      if (
        item.formClass === FormClassRetire["生計維持に関する調査書（様式11号）"]
      ) {
        let keyword =
          demanderRelationship === DemanderRelationship["内縁の妻又は夫"]
            ? "marriageReport"
            : "livelihoodInvestForm";
        targetObj = resArr.find((item) => item.keyword === keyword);
      }

      if (targetObj) {
        targetObj.showLine = true;
        targetObj.dataname = item.formName;
        targetObj.base64filedata = item.file;
        if (targetObj.keyword === "livelihoodInvestForm") {
          targetObj.title = targetObj.title?.replaceAll(
            "{$1}",
            data?.livelihoodFromRetireeFlg === "1" ? "いる" : "いない"
          );
        }
      }
    });
    let identityDocs = data?.fileList?.filter(
      (item) => item.formClass === FormClassRetire["本人確認書類"]
    );
    if (identityDocs?.[0]) {
      let identityDocs1 = resArr.find(
        (item) => item.keyword === "identityDocs1"
      )!;
      identityDocs1.showLine = true;
      identityDocs1.dataname = identityDocs[0].formName;
      identityDocs1.base64filedata = identityDocs[0].file;
    }
    if (identityDocs?.[1]) {
      let identityDocs2 = resArr.find(
        (item) => item.keyword === "identityDocs2"
      )!;
      identityDocs2.showLine = true;
      identityDocs2.dataname = identityDocs[1].formName;
      identityDocs2.base64filedata = identityDocs[1].file;
    }

    setStateTableList([...resArr]);
  };

  useEffect(() => {
    handleReferInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const makeHttpObj = (): IAPI_RET005_Request => {
    const contextFileList = context?.fileList ?? [];
    const fileList = contextFileList.map((item: any) => {
      return {
        serialNo: item.serialNo,
        facilityNo: item.facilityNo,
        staffNo: context?.staffNo,
        formKbn: item.formKbn,
        formClass: item.formClass,
        fileName: item.formName,
        fileBase64: item.file,
        issueDocumentNo: item.issueDocumentNo,
        issueDate: item.issueDate,
      };
    });
    const reqObj: IAPI_RET005_Request = {
      applyId: context?.applyId, // 申請id
      tempSaveStep: undefined, // 一時保存ステップ
      contractorNo: isKiko
        ? businessState.common?.contractorNo
        : state.authInfo?.contractorNo, // 共済契約者番号
      businessType: businessState.common?.businessType,
      staffNo: context?.staffNo, // 退職者法人職員番号
      demanderName: context?.demanderName, // 請求者名
      demanderNameKana: context?.demanderNameKana, // 請求者ｶﾅ
      demanderKbn: context?.demanderKbn, // 請求者区分
      demanderRelationship: context?.demanderRelationship, // 請求者続柄
      demanderRelationshipOthers: context?.demanderRelationshipOthers, // 請求者その他続柄
      demanderBirth: context?.demanderBirth, // 生年月日
      tel: context?.tel, // 電話番号
      demanderEmail: context?.demanderEmail, // 請求者メールアドレス
      agentEmail: context?.agentEmail, // 代行者メールアドレス
      demanderZipcode: context?.demanderZipcode, // 請求者住所（郵便番号）
      demanderPrefectureCd: context?.demanderPrefectureCd, // 請求者住所（都道府県）
      demanderCity: context?.demanderCity, // 請求者住所（市区町村）
      demanderAddressOthers: context?.demanderAddressOthers, // 請求者住所（その他）
      nowZipcode: context?.nowZipcode, // 現住所（郵便番号）
      nowPrefectureCd: context?.nowPrefectureCd, // 現住所（都道府県）
      nowAddressCity: context?.nowAddressCity, // 現住所（市区町村）
      nowAddressOthers: context?.nowAddressOthers, // 現住所（その他）
      domesticResidenceFlg: context?.domesticResidenceFlg, // 請求者国内居住有無
      retirementYearFirstZipcode: context?.retirementYearFirstZipcode, // 退職年度初日住所（郵便番号）
      retirementYearFirstPrefectureCd: context?.retirementYearFirstPrefectureCd, // 退職年度初日住所（都道府県）
      retirementYearFirstAddressCity: context?.retirementYearFirstAddressCity, // 退職年度初日住所（市区町村）
      retirementYearAddressOthers: context?.retirementYearAddressOthers, // 退職年度初日住所（その他）
      retirementReason: context?.retirementReason, // 退職理由（請求者記載）
      nRetireKbn: context?.retireKbn, // 退職手当等の退職の区分
      firstJoinDate: context?.firstJoinDate, // 退職手当等についての勤続期間（自）
      retireDate: context?.retireDate, // 退職手当等についての勤続期間（至）
      livingAssistanceFlg: context?.livingAssistanceFlg, // 生活扶助の有無
      paymentConfirmDate: context?.paymentConfirmDate, // 退職手当等の支払いを受けることになった年月日
      demanderFlg: context?.demanderFlg, // あなたと同順位の請求権保持者の有無
      bankCd: context?.bankCd, // 金融機関コード
      bankName: context?.bankName, // 金融機関名
      branchCd: context?.branchCd, // 支店コード
      branchName: context?.branchName, // 支店名
      accountNameKana: context?.accountNameKana, // 口座名義ｶﾅ
      depositType: context?.depositType, // 預金種目
      accountNo: context?.accountNo, // 口座番号
      livelihoodFromRetireeFlg: context?.livelihoodFromRetireeFlg, // 退職者からの生計維持の有無
      identificationDocsKind: context?.identificationDocsKind, // 本人確認書類の種類
      addPaymentApplyFlg: context?.addPaymentApplyFlg, // 追加支給申請フラグ
      addPaymentReason: context?.addPaymentReason, // 追加支給理由
      addPaymentAmt: context?.addPaymentAmt, // 追加支給金額
      totalPaymentAmtAfterAdd: context?.totalPaymentAmtAfterAdd, // 支給額(追加支給後)
      pastLosInCurrentYearTotalFrom: context?.totalYearLosFrom, // 本年度の退職手当通算勤続期間に通算された前の退職手当勤続期間（自）
      pastLosInCurrentYearTotalTo: context?.totalYearLosTo, // 本年度の退職手当通算勤続期間に通算された前の退職手当勤続期間（至）
      pastLosInCurrentYearOtherFrom: context?.totalCurrentYearLosFrom, // 本年中に支払を受けた他の退職手当勤続期間に通算された前の退職手当勤続期間（自）
      pastLosInCurrentYearOtherTo: context?.totalCurrentYearLosTo, // 本年中に支払を受けた他の退職手当勤続期間に通算された前の退職手当勤続期間（至）
      currentYearLosFrom: context?.currentYearLosFrom, // 本年中に支払を受けた他の退職手当等についての勤続期間（自）
      currentYearLosTo: context?.currentYearLosTo, // 本年中に支払を受けた他の退職手当等についての勤続期間（至）
      currentYearPaymentDecisionDate: context?.currentYearPaymentDecisionDate, // 本年中に支払を受けた他の退職手当等の支払を受けることとなった年月日
      currentYearRetirementAllowanceAmt:
        context?.currentYearRetirementAllowanceAmt, // 本年中に支払を受けた他の退職手当等の収入金額
      currentYearWithholdingAmt: context?.currentYearWithholdingAmt, // 本年中に支払を受けた他の退職手当等の源泉徴収額
      currentYearScCityAmt: context?.currentYearScCityAmt, // 本年中に支払を受けた他の退職手当等の特別徴収税額（市町村民税）
      currentYearScPrefectureAmt: context?.currentYearScPrefectureAmt, // 本年中に支払を受けた他の退職手当等特別徴収税額（道府県民税）
      currentYearGetPaidDate: context?.currentYearGetPaidDate, // 本年中に支払を受けた他の退職手当等の支払いを受けた年月日
      currentYearRetirementKbn: context?.currentYearRetirementKbn, // 本年中に支払を受けた他の退職手当等の退職の区分
      currentYearPayerZipcode: context?.currentYearPayerZipcode, // 本年中に支払を受けた他の退職手当等の支払者の住所(郵便番号)
      currentYearPayerPrefectureCd: context?.currentYearPayerPrefectureCd, // 本年中に支払を受けた他の退職手当等の支払者の住所(都道府県)
      currentYearPayerAddressCity: context?.currentYearPayerAddressCity, // 本年中に支払を受けた他の退職手当等の支払者の住所(市区町村)
      currentYearPayerAddressOthers: context?.currentYearPayerAddressOthers, // 本年中に支払を受けた他の退職手当等の支払者の住所(その他)
      currentYearPayerName: context?.past4yearsPayerName, // 本年中に支払を受けた他の退職手当等の支払者の名称(氏名)
      past4yearsLosFrom: context?.past4yearsLosFrom, // 前年以前4年内の退職手当等についての勤続期間（自）
      past4yearsLosTo: context?.past4yearsLosTo, // 前年以前4年内の退職手当等についての勤続期間（至）
      past4yearsPaymentDecisionDate: context?.past4yearsPaymentDecisionDate, // 前年以前4年内の退職手当等の支払を受けることとなった年月日
      past4yearsRetirementAllowanceAmt:
        context?.past4yearsRetirementAllowanceAmt, // 前年以前4年内の退職手当等の収入金額
      past4yearsWithholdingAmt: context?.past4yearsWithholdingAmt, // 前年以前4年内の退職手当等の源泉徴収額
      past4yearsScCityAmt: context?.past4yearsScPrefectureAmt, // 前年以前4年内の退職手当等の特別徴収税額（市町村民税）
      past4yearsScPrefectureAmt: context?.past4yearsScCityAmt, // 前年以前4年内の退職手当等の特別徴収税額（道府県民税）
      past4yearsGetPaidDate: context?.past4yearsGetPaidDate, // 前年以前4年内の退職手当等の支払いを受けた年月日
      past4yearsRetirementKbn: context?.past4yearsRetirementKbn, // 前年以前4年内の退職手当等の退職の区分
      past4yearsPayerZipcode: context?.past4yearsPayerZipcode, // 前年以前4年内の退職手当等の支払者の住所(郵便番号)
      past4yearsPayerPrefectureCd: context?.past4yearsPayerPrefectureCd, // 前年以前4年内の退職手当等の支払者の住所(都道府県)
      past4yearsPayerAddressCity: context?.past4yearsPayerAddressCity, // 前年以前4年内の退職手当等の支払者の住所(市区町村)
      past4yearsPayerAddressOthers: context?.past4yearsPayerAddressOthers, // 前年以前4年内の退職手当等の支払者の住所(その他)
      past4yearsPayerName: context?.past4yearsPayerName, // 前年以前4年内の退職手当等の支払者の名称(氏名)
      retirementFormApplyId: context?.retirementFormApplyId, // 被共済職員退職届申請id
      page1Kbn: context?.page1Kbn, // 1枚目源泉徴収票・特別徴収票の区分
      page1PaymentAmt: context?.page1PaymentAmt, // 1枚目の支払金額
      page1WithholdingAmt: context?.page1WithholdingAmt, // 1枚目の源泉徴収税額
      page1ScAmtCity: context?.page1ScAmtCity, // 1枚目の特別徴収税額（市町村民税）
      page1ScAmtPrefecture: context?.page1ScAmtPrefecture, // 1枚目の特別徴収税額（道府県民税）
      page1LengthOfService: context?.page1LengthOfService, // 1枚目の勤続年数
      page1EmployedDate: context?.page1EmployedDate, // 1枚目の就職年月日
      page1RetiredDate: context?.page1RetiredDate, // 1枚目の退職年月日
      page2Kbn: context?.page2Kbn, // 2枚目源泉徴収票・特別徴収票の区分
      page2PaymentAmt: context?.page2PaymentAmt, // 2枚目の支払金額
      page2WithholdingAmt: context?.page2WithholdingAmt, // 2枚目の源泉徴収税額
      page2ScAmtCity: context?.page2ScAmtCity, // 2枚目の特別徴収税額（市町村民税
      page2ScAmtPrefecture: context?.page2ScAmtPrefecture, // 2枚目の特別徴収税額（道府県民税）
      page2LengthOfService: context?.page2LengthOfService, // 2枚目の勤続年数
      page2EmployedDate: context?.page2EmployedDate, // 2枚目の就職年月日
      page2RetiredDate: context?.page2RetiredDate, // 2枚目の退職年月日
      page3Kbn: context?.page3Kbn, // 3枚目源泉徴収票・特別徴収票の区分
      page3PaymentAmt: context?.page3PaymentAmt, // 3枚目の支払金額
      page3WithholdingAmt: context?.page3WithholdingAmt, // 3枚目の源泉徴収税額
      page3ScAmtCity: context?.page3ScAmtCity, // 3枚目の特別徴収税額（市町村民税）
      page3ScAmtPrefecture: context?.page3ScAmtPrefecture, // 3枚目の特別徴収税額（道府県民税）
      page3LengthOfService: context?.page3LengthOfService, // 3枚目の勤続年数
      page3EmployedDate: context?.page3EmployedDate, // 3枚目の就職年月日
      page3RetiredDate: context?.page3RetiredDate, // 3枚目の退職年月日
      contractorName: context?.contractorName, // 契約者（法人）名
      fileList: fileList, // ファイルリスト
      receptionDate: "", // 受付日
      screenId: "SCRR410027", // 画面id
    };

    return reqObj;
  };

  const onCheckAndSubmit = () => {
    setHttpReq({ ...makeHttpObj() });
    setOpen(true);
  };

  const handleOk = async (date?: any) => {
    // 退職手当金請求更新API実行
    try {
      const param = API_RET005_BuildReqParams({
        ...httpReq,
        tempSaveStep: null as any,
        receptionDate: date,
      });
      const res = await httpAPI_RET005(param);

      if (res.resultCd === "I0000") {
        setState({
          ...state,
          errorMsgId: "",
        });
        navigate("/SCRC070008");
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
        setOpen(false);
      }
    } catch (error) {
      navigate("/error");
    }
  };

  return (
    <Box data-testid="SCRR410027PageForTest">
      <TkStepper steps={steps} activeStep={applyId ? 9 : 8} />
      <TkInfoManual rule={"\\n"}>
        {isKiko
          ? labelids.labelid.LBLR410027_02
          : labelids.labelid.LBLR410027_01}
      </TkInfoManual>
      <TkFakeTable
        list={stateTableList}
        ruleSchema={{}}
        onCheckAndSubmit={onCheckAndSubmit}
        buttonGroup={<TkBackButton />}
        submitButtonText={isKiko ? "申請" : "機構へ提出する"}
        showButton={!applyId}
      />

      {isKiko ? (
        <TkDateDialog
          open={open}
          onOky={handleOk}
          onCancel={() => setOpen(false)}
          minDate={new Date("1900/01/01")}
          maxDate={new Date()}
          title="確認（申請）"
          contextText="申請を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      ) : (
        <TkMessageDialog
          open={open}
          onOky={handleOk}
          onCancel={() => setOpen(false)}
          title="確認（機構へ提出する）"
          contextText="機構へ提出を行いますが、問題ないでしょうか。"
          cancelText="いいえ"
          okText="はい"
        />
      )}
    </Box>
  );
};

export default SCRR410027;
